import { BodyBox, EntityForm, Notice } from '@lbcamden/lib-react';
import { formatDMY } from '@lbcamden/shared-utils';
import { FunctionComponent } from 'react';
import { useParams } from 'react-router';

import { useProperty, useUserStore, useVisit } from '~/client/hooks/storeHooks';
import { useGoBackOrNavigate } from '~/client/hooks/useGoBackOrNavigate';
import { getPropertyBreadcrumbs, toVisitPage } from '~/client/utils/navigation';

import { PageBreadcrumbs } from '../../shared/PageBreadcrumbs';
import { LoadInNotFoundPage } from '../LoadInNotFoundPage';
import {
  useConvertVisitFormData,
  getVisitFormConfig,
  VisitFormValues,
  visitFormSchema,
} from './config';

const title = 'Edit visit';

export const PropertyVisitEditPage: FunctionComponent = () => {
  const navigate = useGoBackOrNavigate();

  const { propertyId: propertyIdParam, visitId: visitIdParam } = useParams();
  const propertyId = parseInt(propertyIdParam!);

  const property = useProperty(propertyId);
  const visit = useVisit(visitIdParam!);
  const client = useUserStore();

  const convert = useConvertVisitFormData({
    propertyId: Number(propertyIdParam),
    visitId: visit?.visitId,
  });

  if (!property || !visit) {
    return <LoadInNotFoundPage />;
  }

  const onCancel = () => navigate(toVisitPage(propertyId, visitIdParam!));

  const onSave = (data: VisitFormValues) => {
    client?.handleAndDispatchMessage('editVisitLog', visitIdParam!, convert.fromForm(data, visit));
    void navigate(toVisitPage(propertyId, visitIdParam!));
  };

  return (
    <BodyBox>
      <PageBreadcrumbs
        css={{ marginBottom: 0 }}
        links={[
          ...getPropertyBreadcrumbs(property),
          {
            link: toVisitPage(propertyId, visit.visitId),
            name: `Visit ${formatDMY(visit.timestamp)}`,
          },
          { name: title },
        ]}
      />
      <EntityForm
        title={title}
        message={
          <Notice infoWithIcon>
            Logging a successful visit will automatically close any open “Tenant visit” cases
          </Notice>
        }
        schema={visitFormSchema}
        config={getVisitFormConfig()}
        initialValue={convert.toForm(visit)}
        onCancel={onCancel}
        onSave={onSave}
      />
    </BodyBox>
  );
};
